
// Deployment trigger: 2026-04-24
import React, { useState, useEffect } from 'react';
import { Project } from '../types';
import { Globe, Plus, Trash2, Edit3, Search, ArrowRight, X, Mail, Smartphone, UserPlus, ShieldCheck, User, LayoutGrid, Shuffle, AlertCircle, CheckCircle2 } from 'lucide-react';
import { db, auth, secondaryAuth } from '../firebase';
import { collection, addDoc, deleteDoc, doc, updateDoc, serverTimestamp, setDoc, query, where, getDocs, limit } from 'firebase/firestore';
import { createUserWithEmailAndPassword, updateProfile } from 'firebase/auth';
import { setWorkspaceCredentials } from '../services/credentialService';

interface ProjectManagerProps {
  projects: Project[];
  onSelect: (id: string) => void;
  activeProjectId: string | 'none';
  isAdmin?: boolean;
}

const ProjectManager: React.FC<ProjectManagerProps> = ({ projects, onSelect, activeProjectId, isAdmin = false }) => {
  // Maps a workspace user's email -> their account doc id + current role.
  const [userRoles, setUserRoles] = useState<Record<string, { uid: string; role: string }>>({});
  const [roleUpdating, setRoleUpdating] = useState<string | null>(null);
  const [showAdd, setShowAdd] = useState(false);
  const [editingProject, setEditingProject] = useState<Project | null>(null);
  const [newProject, setNewProject] = useState({ name: '', domain: 'myeplay.myesalon.com' });
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const [newUserEmail, setNewUserEmail] = useState('');
  const [newUserPassword, setNewUserPassword] = useState('');
  const [addUserLoading, setAddUserLoading] = useState(false);

  const handleAdd = async () => {
    if (!newProject.name || !auth.currentUser) return;
    setLoading(true);
    try {
      await addDoc(collection(db, 'projects'), {
        name: newProject.name,
        domain: newProject.domain || 'myeplay.myesalon.com',
        ownerId: auth.currentUser.uid,
        allowedEmails: [auth.currentUser.email],
        createdAt: new Date().toISOString().split('T')[0],
        emailQuota: 2000, // Default 2000
        smsQuota: 200,    // Default 200
        emailUsed: 0,
        smsUsed: 0,
        serverCreatedAt: serverTimestamp()
      });
      setNewProject({ name: '', domain: 'myeplay.myesalon.com' });
      setShowAdd(false);
    } catch (err) {
      console.error(err);
      alert("Error adding project.");
    } finally {
      setLoading(false);
    }
  };

  const handleAddUser = async () => {
    if (!editingProject || !newUserEmail || !newUserPassword) return;
    setAddUserLoading(true);
    try {
      let emailToUse = newUserEmail.trim().toLowerCase();
      let usernameToUse = newUserEmail.trim();

      // If it doesn't look like an email, treat it as a username and generate a dummy email
      if (!emailToUse.includes('@')) {
        emailToUse = `${usernameToUse.toLowerCase().replace(/\s+/g, '')}@myeplay.local`;
      } else {
        usernameToUse = emailToUse.split('@')[0];
      }

      // Try to create the user in Firebase Auth using the secondary instance
      const userCredential = await createUserWithEmailAndPassword(secondaryAuth, emailToUse, newUserPassword);
      
      try {
        await updateProfile(userCredential.user, { displayName: usernameToUse });

        // Save username mapping so they can log in with their username
        if (!newUserEmail.includes('@')) {
          await setDoc(doc(db, 'usernames', usernameToUse.toLowerCase()), {
            email: emailToUse,
            uid: userCredential.user.uid
          });
        }

        await setDoc(doc(db, 'users', userCredential.user.uid), {
          username: usernameToUse,
          email: emailToUse,
          createdAt: serverTimestamp()
        });

        // Store external-API credentials securely (encrypted at rest, server-side).
        // The password is never written to Firestore in plaintext from the browser.
        await setWorkspaceCredentials({
          targetEmail: emailToUse,
          targetUsername: usernameToUse,
          apiUsername: usernameToUse,
          apiPassword: newUserPassword,
        });
      } catch (e) {
        console.error("Failed to save user profile or mapping", e);
      }

      // User created successfully, add to allowedEmails in Firestore immediately to prevent loss on refresh
      const updatedEmails = [...(editingProject.allowedEmails || []), emailToUse];
      await updateDoc(doc(db, 'projects', editingProject.id), {
        allowedEmails: updatedEmails,
        updatedAt: serverTimestamp()
      });
      
      setEditingProject({ ...editingProject, allowedEmails: updatedEmails });
      setNewUserEmail('');
      setNewUserPassword('');
      alert(`Account created for ${newUserEmail} and added to workspace.`);
    } catch (err: any) {
      if (err.code === 'auth/email-already-in-use') {
        let emailToUse = newUserEmail.trim().toLowerCase();
        let usernameToUse = newUserEmail.trim();
        if (!emailToUse.includes('@')) {
          emailToUse = `${newUserEmail.trim().toLowerCase().replace(/\s+/g, '')}@myeplay.local`;
        } else {
          usernameToUse = emailToUse.split('@')[0];
        }
        
        // Account already exists
        console.log(`[Sync] Handling existing account for ${newUserEmail}. Current user: ${auth.currentUser?.email} (${auth.currentUser?.uid})`);
        if (window.confirm(`An account with identifier ${newUserEmail} already exists. Do you want to update this business owner's API credentials and ensure they are in the workspace?`)) {
          try {
            // Store/refresh the external-API credentials securely. The backend
            // finds or creates the user document, encrypts the password at rest,
            // and removes any legacy plaintext password field.
            await setWorkspaceCredentials({
              targetEmail: emailToUse,
              targetUsername: usernameToUse,
              apiUsername: usernameToUse,
              apiPassword: newUserPassword,
            });
            console.log(`[Sync] Stored API credentials for user: ${emailToUse}`);

            // 2. Update the project's allowedEmails in Firestore
            const currentEmails = editingProject.allowedEmails || [];
            if (!currentEmails.includes(emailToUse)) {
              const updatedEmails = [...currentEmails, emailToUse];
              await updateDoc(doc(db, 'projects', editingProject.id), {
                allowedEmails: updatedEmails,
                updatedAt: serverTimestamp()
              });
              // Update local state
              setEditingProject({ ...editingProject, allowedEmails: updatedEmails });
            }

            setNewUserEmail('');
            setNewUserPassword('');
            alert(`API credentials updated for ${newUserEmail} and user is confirmed in the workspace.`);
          } catch (updateErr) {
            console.error("Error updating project with existing user:", updateErr);
            alert("Failed to update user credentials. Please try again.");
          }
        }
      } else {
        console.error("Error creating user:", err);
        alert(`Failed to create user: ${err.message}`);
      }
    } finally {
      setAddUserLoading(false);
    }
  };

  const removeEmail = async (emailToRemove: string) => {
    if (!editingProject) return;
    const updatedEmails = (editingProject.allowedEmails || []).filter(e => e !== emailToRemove);
    
    try {
      await updateDoc(doc(db, 'projects', editingProject.id), {
        allowedEmails: updatedEmails,
        updatedAt: serverTimestamp()
      });
      setEditingProject({
        ...editingProject,
        allowedEmails: updatedEmails
      });
    } catch (err) {
      console.error("Failed to remove email:", err);
      alert("Failed to remove user from workspace.");
    }
  };

  // Load the role of each workspace user when an admin opens a project.
  useEffect(() => {
    if (!editingProject || !isAdmin) {
      setUserRoles({});
      return;
    }
    let cancelled = false;
    (async () => {
      const emails = editingProject.allowedEmails || [];
      const results: Record<string, { uid: string; role: string }> = {};
      await Promise.all(
        emails.map(async (email) => {
          try {
            const snap = await getDocs(query(collection(db, 'users'), where('email', '==', email), limit(1)));
            if (!snap.empty) {
              const d = snap.docs[0];
              results[email] = { uid: d.id, role: (d.data().role as string) || 'user' };
            }
          } catch {
            /* ignore individual lookup failures */
          }
        })
      );
      if (!cancelled) setUserRoles(results);
    })();
    return () => {
      cancelled = true;
    };
  }, [editingProject, isAdmin]);

  const toggleRole = async (email: string) => {
    const entry = userRoles[email];
    if (!entry) {
      alert("This user has no account record yet. Ask them to log in once, then try again.");
      return;
    }
    const newRole = entry.role === 'admin' ? 'user' : 'admin';
    setRoleUpdating(email);
    try {
      await updateDoc(doc(db, 'users', entry.uid), { role: newRole, updatedAt: serverTimestamp() });
      setUserRoles(prev => ({ ...prev, [email]: { ...entry, role: newRole } }));
    } catch (err) {
      console.error("Failed to update role:", err);
      alert("Failed to update role. Only admins can change roles.");
    } finally {
      setRoleUpdating(null);
    }
  };

  const handleUpdate = async () => {
    if (!editingProject || !editingProject.name) return;
    setLoading(true);
    try {
      const { id, ...data } = editingProject;
      await updateDoc(doc(db, 'projects', id), { ...data, updatedAt: serverTimestamp() });
      setEditingProject(null);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const deleteProject = async (id: string) => {
    if (confirm("Are you sure? This will delete the workspace and all associated data.")) {
      try {
        await deleteDoc(doc(db, 'projects', id));
      } catch (err) {
        console.error(err);
      }
    }
  };

  const filteredProjects = projects.filter(p => 
    p.name.toLowerCase().includes(searchTerm.toLowerCase()) || 
    p.domain.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="space-y-10">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6">
        <div className="relative w-full max-w-xl">
          <Search className="absolute left-5 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
          <input 
            type="text" 
            placeholder="Search projects..." 
            className="w-full pl-14 pr-6 py-4 rounded-2xl border border-slate-200 outline-none focus:ring-4 focus:ring-indigo-500/10 focus:border-indigo-500 transition-all bg-white text-slate-800 text-base shadow-sm font-medium"
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
          />
        </div>
        
        <button 
          onClick={() => { setShowAdd(!showAdd); setEditingProject(null); }}
          className="flex items-center gap-3 bg-slate-900 text-white px-8 py-4 rounded-2xl text-sm font-black uppercase tracking-widest shadow-xl hover:bg-slate-800 transition-all w-full md:w-auto justify-center"
        >
          {showAdd ? 'Close' : <><Plus className="w-5 h-5" /> Add Project</>}
        </button>
      </div>

      {showAdd && (
        <div className="bg-white p-8 sm:p-12 rounded-[40px] border-2 border-indigo-100 shadow-2xl animate-in slide-in-from-top-4 duration-500">
           <div className="text-center mb-10">
              <h3 className="text-2xl font-black text-slate-800 uppercase italic tracking-tight">New Workspace</h3>
           </div>
           
           <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-10">
              <div>
                <label className="text-[10px] font-black text-slate-500 mb-2 block uppercase tracking-widest px-1">Business Name</label>
                <input 
                  type="text" 
                  value={newProject.name}
                  onChange={e => setNewProject({...newProject, name: e.target.value})}
                  className="w-full px-6 py-5 rounded-2xl border border-slate-200 bg-slate-50/30 text-slate-800 placeholder:text-slate-300 focus:bg-white focus:border-indigo-500 outline-none transition-all text-base font-bold shadow-inner" 
                  placeholder="Acme Game Project"
                />
              </div>
              <div>
                <label className="text-[10px] font-black text-slate-500 mb-2 block uppercase tracking-widest px-1">Domain URL</label>
                <input 
                  type="text" 
                  value={newProject.domain}
                  onChange={e => setNewProject({...newProject, domain: e.target.value})}
                  className="w-full px-6 py-5 rounded-2xl border border-slate-200 bg-slate-50/30 text-slate-800 focus:bg-white focus:border-indigo-500 outline-none transition-all text-base font-bold shadow-inner" 
                  placeholder="myeplay.myesalon.com"
                />
              </div>
           </div>
           <button 
             onClick={handleAdd}
             disabled={!newProject.name || loading}
             className="w-full bg-indigo-600 text-white py-6 rounded-2xl font-black uppercase tracking-[0.2em] hover:bg-indigo-700 transition-all disabled:opacity-50"
           >
             {loading ? 'Creating...' : 'Create Workspace'}
           </button>
        </div>
      )}

      {editingProject && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4 z-[100]">
          <div className="bg-white w-full max-w-2xl rounded-[40px] shadow-2xl p-8 sm:p-12 animate-in zoom-in-95 duration-300">
            <div className="flex justify-between items-center mb-8">
              <h3 className="text-2xl font-black text-slate-800 uppercase italic">Edit Workspace</h3>
              <button onClick={() => setEditingProject(null)} className="p-2 text-slate-400 hover:text-slate-600"><X className="w-6 h-6" /></button>
            </div>
            <div className="space-y-6 mb-10">
              <div>
                <label className="text-[10px] font-black text-slate-500 mb-2 block uppercase tracking-widest">Business Name</label>
                <input type="text" value={editingProject.name} onChange={e => setEditingProject({...editingProject, name: e.target.value})} className="w-full px-6 py-4 rounded-2xl border border-slate-200 bg-slate-50 text-slate-800 font-bold outline-none" />
              </div>
              <div>
                <label className="text-[10px] font-black text-slate-500 mb-2 block uppercase tracking-widest">Domain</label>
                <input type="text" value={editingProject.domain} onChange={e => setEditingProject({...editingProject, domain: e.target.value})} className="w-full px-6 py-4 rounded-2xl border border-slate-200 bg-slate-50 text-slate-800 font-bold outline-none" placeholder="myeplay.myesalon.com" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                 <div>
                    <label className="text-[10px] font-black text-slate-500 mb-2 block uppercase tracking-widest">Email Quota</label>
                    <input type="number" value={editingProject.emailQuota} onChange={e => setEditingProject({...editingProject, emailQuota: parseInt(e.target.value)})} className="w-full px-6 py-4 rounded-2xl border border-slate-200 bg-slate-50 text-slate-800 font-bold outline-none" />
                 </div>
                 <div>
                    <label className="text-[10px] font-black text-slate-500 mb-2 block uppercase tracking-widest">SMS Quota</label>
                    <input type="number" value={editingProject.smsQuota} onChange={e => setEditingProject({...editingProject, smsQuota: parseInt(e.target.value)})} className="w-full px-6 py-4 rounded-2xl border border-slate-200 bg-slate-50 text-slate-800 font-bold outline-none" />
                 </div>
              </div>

              {/* Embed Display: how this project's site behaves with multiple active games */}
              <div className="pt-4 border-t border-slate-100">
                <label className="text-[10px] font-black text-slate-500 mb-1 block uppercase tracking-widest">Embed Display</label>
                <p className="text-[11px] text-slate-400 font-medium mb-3 leading-relaxed">
                  How this project's website behaves when more than one game is active.
                </p>
                <div className="space-y-2">
                  {([
                    { mode: 'all' as const, icon: LayoutGrid, title: 'Show All Available Games', desc: 'Display every active game, each with its own launcher.' },
                    { mode: 'random' as const, icon: Shuffle, title: 'Randomly Pick 1', desc: 'Show one random game per visit (A/B testing).' },
                  ]).map(opt => {
                    const on = (editingProject.embedDisplayMode ?? 'all') === opt.mode;
                    const Icon = opt.icon;
                    return (
                      <button
                        key={opt.mode}
                        type="button"
                        onClick={() => setEditingProject({ ...editingProject, embedDisplayMode: opt.mode })}
                        aria-pressed={on}
                        className={`w-full flex items-center gap-3 p-3 rounded-2xl border text-left transition-all ${on ? 'border-indigo-500 bg-indigo-50/60 ring-4 ring-indigo-500/10' : 'border-slate-200 bg-slate-50/50 hover:border-slate-300'}`}
                      >
                        <div className={`p-2 rounded-xl shrink-0 ${on ? 'bg-indigo-600' : 'bg-slate-200'}`}>
                          <Icon className={`w-4 h-4 ${on ? 'text-white' : 'text-slate-500'}`} />
                        </div>
                        <div className="min-w-0">
                          <div className={`text-xs font-black ${on ? 'text-indigo-700' : 'text-slate-700'}`}>{opt.title}</div>
                          <div className="text-[10px] text-slate-400 font-medium">{opt.desc}</div>
                        </div>
                        {on && <CheckCircle2 className="w-4 h-4 text-indigo-600 ml-auto shrink-0" />}
                      </button>
                    );
                  })}
                </div>
                {(editingProject.embedDisplayMode ?? 'all') === 'all' && (
                  <div className="mt-3 p-3 bg-amber-50 rounded-2xl border border-amber-200 flex items-start gap-2.5">
                    <AlertCircle className="w-4 h-4 text-amber-500 shrink-0 mt-0.5" />
                    <p className="text-[10px] text-amber-700 font-medium leading-relaxed">
                      <span className="font-black">All active games will be shown.</span> Each one adds its own launcher to the site. For the cleanest visitor experience, we recommend keeping a single game active at a time — multiple simultaneous games can clutter the page and distract from conversions.
                    </p>
                  </div>
                )}
              </div>

              <div className="pt-4 border-t border-slate-100">
                <h4 className="text-sm font-black text-slate-800 uppercase tracking-widest mb-4">Workspace Users</h4>
                
                {/* List of current users */}
                <div className="space-y-2 mb-6 max-h-32 overflow-y-auto custom-scrollbar">
                  {(editingProject.allowedEmails || []).length === 0 ? (
                    <p className="text-sm text-slate-400 italic">No users added yet.</p>
                  ) : (
                    (editingProject.allowedEmails || []).map((email, idx) => {
                      const displayEmail = email.endsWith('@myeplay.local') ? email.split('@')[0] : email;
                      const roleEntry = userRoles[email];
                      const isSelf = roleEntry && roleEntry.uid === auth.currentUser?.uid;
                      return (
                        <div key={idx} className="flex items-center justify-between bg-slate-50 px-4 py-2 rounded-xl border border-slate-100 gap-2">
                          <span className="text-sm font-bold text-slate-700 truncate">{displayEmail}</span>
                          <div className="flex items-center gap-1 shrink-0">
                            {isAdmin && roleEntry && (
                              isSelf ? (
                                <span className="flex items-center gap-1 text-[9px] font-black uppercase tracking-widest text-indigo-600 bg-indigo-50 px-2 py-1 rounded-lg">
                                  <ShieldCheck className="w-3 h-3" /> You
                                </span>
                              ) : (
                                <button
                                  onClick={() => toggleRole(email)}
                                  disabled={roleUpdating === email}
                                  title={roleEntry.role === 'admin' ? 'Admin — click to make User' : 'User — click to make Admin'}
                                  className={`flex items-center gap-1 text-[9px] font-black uppercase tracking-widest px-2 py-1 rounded-lg transition-all disabled:opacity-50 ${
                                    roleEntry.role === 'admin'
                                      ? 'text-emerald-700 bg-emerald-50 hover:bg-emerald-100'
                                      : 'text-slate-500 bg-white border border-slate-200 hover:bg-slate-100'
                                  }`}
                                >
                                  {roleEntry.role === 'admin' ? <ShieldCheck className="w-3 h-3" /> : <User className="w-3 h-3" />}
                                  {roleUpdating === email ? '...' : roleEntry.role === 'admin' ? 'Admin' : 'User'}
                                </button>
                              )
                            )}
                            <button
                              onClick={() => removeEmail(email)}
                              className="text-slate-400 hover:text-red-500 transition-colors p-1"
                            >
                              <X className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>

                {/* Add new user form */}
                <div className="bg-indigo-50/50 p-4 rounded-2xl border border-indigo-100/50">
                  <label className="text-[10px] font-black text-indigo-600 mb-3 block uppercase tracking-widest">Add Business Owner Account</label>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
                    <input 
                      type="text" 
                      value={newUserEmail} 
                      onChange={e => setNewUserEmail(e.target.value)} 
                      className="w-full px-4 py-3 rounded-xl border border-indigo-100 bg-white text-slate-800 font-medium outline-none text-sm focus:border-indigo-400" 
                      placeholder="Username or Email" 
                    />
                    <input 
                      type="text" 
                      value={newUserPassword} 
                      onChange={e => setNewUserPassword(e.target.value)} 
                      className="w-full px-4 py-3 rounded-xl border border-indigo-100 bg-white text-slate-800 font-medium outline-none text-sm focus:border-indigo-400" 
                      placeholder="Password" 
                    />
                  </div>
                  <button 
                    onClick={handleAddUser}
                    disabled={addUserLoading || !newUserEmail || !newUserPassword}
                    className="w-full bg-indigo-600 hover:bg-indigo-700 disabled:bg-indigo-300 text-white py-3 rounded-xl font-bold uppercase tracking-wider text-xs transition-all flex items-center justify-center gap-2"
                  >
                    {addUserLoading ? 'Processing...' : <><UserPlus className="w-4 h-4" /> Create & Add User</>}
                  </button>
                  <p className="text-[10px] text-indigo-400 mt-2 font-medium text-center">
                    Creates a new account. These credentials will also be used for external API synchronization (myeSalon).
                  </p>
                </div>
              </div>
            </div>
            <button onClick={handleUpdate} disabled={loading} className="w-full bg-slate-900 text-white py-5 rounded-2xl font-black uppercase tracking-[0.2em] hover:bg-slate-800 transition-all shadow-xl mt-6">
               {loading ? 'Updating...' : 'Save Workspace Changes'}
            </button>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
        {filteredProjects.map((p) => (
          <div 
            key={p.id}
            onClick={() => onSelect(p.id)}
            className={`group bg-white rounded-[32px] border-2 transition-all p-8 cursor-pointer hover:shadow-2xl hover:-translate-y-2 relative overflow-hidden flex flex-col ${activeProjectId === p.id ? 'border-indigo-500 bg-indigo-50/10' : 'border-slate-100 shadow-sm'}`}
          >
            <div className="flex justify-between items-start mb-6">
               <div className={`w-14 h-14 rounded-2xl flex items-center justify-center transition-colors ${activeProjectId === p.id ? 'bg-indigo-600 text-white' : 'bg-indigo-50 text-indigo-600'}`}>
                  <Globe className="w-7 h-7" />
               </div>
               <div className="flex gap-1">
                 <button onClick={(e) => { e.stopPropagation(); setEditingProject(p); }} className="p-2 text-slate-300 hover:text-indigo-500 hover:bg-indigo-50 rounded-xl transition-all"><Edit3 className="w-5 h-5" /></button>
                 <button onClick={(e) => { e.stopPropagation(); deleteProject(p.id); }} className="p-2 text-slate-300 hover:text-red-500 hover:bg-red-50 rounded-xl transition-all"><Trash2 className="w-5 h-5" /></button>
               </div>
            </div>
            <h4 className="font-black text-slate-800 text-xl mb-1 tracking-tight">{p.name}</h4>
            <p className="text-sm text-slate-400 mb-6 flex items-center gap-2 font-medium truncate italic">{p.domain}</p>
            
            <div className="mt-auto space-y-4 pt-4 border-t border-slate-50">
               <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2 text-[10px] font-black text-slate-400 uppercase tracking-widest">
                     <Mail className="w-3 h-3 text-indigo-500" /> Emails Used
                  </div>
                  <span className="text-[10px] font-black text-slate-900">{p.emailUsed} / {p.emailQuota}</span>
               </div>
               <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2 text-[10px] font-black text-slate-400 uppercase tracking-widest">
                     <Smartphone className="w-3 h-3 text-emerald-500" /> SMS Used
                  </div>
                  <span className="text-[10px] font-black text-slate-900">{p.smsUsed} / {p.smsQuota}</span>
               </div>
            </div>

            <div className={`mt-6 w-full py-4 rounded-2xl font-black text-xs uppercase tracking-[0.15em] transition-all flex items-center justify-center gap-2 ${activeProjectId === p.id ? 'bg-indigo-600 text-white' : 'bg-slate-900 text-white'}`}>
              Enter Workspace
              <ArrowRight className="w-4 h-4" />
            </div>
          </div>
        ))}
        {projects.length === 0 && !showAdd && (
          <button onClick={() => setShowAdd(true)} className="bg-slate-50 rounded-[32px] border-4 border-dashed border-slate-200 p-12 flex flex-col items-center justify-center gap-4 text-slate-400 hover:border-indigo-300 transition-all min-h-[300px] w-full">
            <Plus className="w-10 h-10" />
            <span className="font-black text-sm uppercase tracking-widest">Create First Project</span>
          </button>
        )}
      </div>
    </div>
  );
};

export default ProjectManager;
